import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Check, UserPlus } from 'lucide-react'
import type { ClientProfile } from '../../types/case-management'
import { INPUT_STYLE } from './new-case-modal.constants'

function ClientTriggerButton({
  selectedClient,
  onClick,
  disabled,
}: {
  selectedClient: ClientProfile | undefined
  onClick: () => void
  disabled: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        className="w-full rounded-lg border px-4 py-2.5 pr-10 text-left transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        style={INPUT_STYLE}
        disabled={disabled}
      >
        {selectedClient ? (
          <span>
            {selectedClient.name}
            {selectedClient.shortName && (
              <span style={{ color: 'var(--text-muted, #8e93a6)' }} className="ml-2">
                ({selectedClient.shortName})
              </span>
            )}
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted, #8e93a6)' }}>{t('newCase.selectClient')}</span>
        )}
      </button>
      <ChevronDown
        className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2"
        style={{ color: 'var(--text-muted, #8e93a6)' }}
      />
    </div>
  )
}

interface NewClientFormProps {
  newClientName: string
  setNewClientName: (value: string) => void
  newClientShortName: string
  setNewClientShortName: (value: string) => void
  onCancel: () => void
  onCreate: () => void
  isCreatingClient: boolean
}

export function NewClientForm(props: NewClientFormProps): ReactElement {
  const { t } = useTranslation('cases')
  const submitDisabled = !props.newClientName.trim() || props.isCreatingClient
  return (
    <div
      className="border-b p-3"
      style={{
        borderColor: 'var(--border, rgba(28,31,46,0.09))',
        background: 'var(--highlight, rgba(78,94,130,0.08))',
      }}
    >
      <div className="space-y-2">
        <input
          type="text"
          value={props.newClientName}
          onChange={(e) => {
            props.setNewClientName(e.target.value)
          }}
          placeholder={t('newCase.clientNameRequired')}
          className="w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:ring-[#4e5e82] focus:outline-none"
          style={INPUT_STYLE}
          autoFocus
        />
        <input
          type="text"
          value={props.newClientShortName}
          onChange={(e) => {
            props.setNewClientShortName(e.target.value)
          }}
          placeholder={t('newCase.shortNameOptional')}
          className="w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:ring-[#4e5e82] focus:outline-none"
          style={INPUT_STYLE}
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={props.onCancel}
            className="flex-1 rounded-md px-3 py-1.5 text-sm transition-colors"
            style={{ color: 'var(--text-dim, #555c72)' }}
          >
            {t('actions.cancel')}
          </button>
          <button
            type="button"
            onClick={props.onCreate}
            disabled={submitDisabled}
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[#4e5e82] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {props.isCreatingClient ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <>
                <Check className="h-3 w-3" />
                {t('actions.create')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

function CreateClientButton({ onClick }: { onClick: () => void }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 border-b px-4 py-2.5 text-left font-medium transition-colors"
      style={{ color: 'var(--accent, #4e5e82)', borderColor: 'var(--border, rgba(28,31,46,0.09))' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--hover, rgba(78,94,130,0.06))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >
      <UserPlus className="h-4 w-4" />
      {t('newCase.createNewClient')}
    </button>
  )
}

function ClientListItem({
  client,
  onSelect,
}: {
  client: ClientProfile
  onSelect: () => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full px-4 py-2.5 text-left transition-colors"
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--hover, rgba(78,94,130,0.06))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >
      <span style={{ color: 'var(--text, #1c1f2e)' }}>{client.name}</span>
      {client.shortName && (
        <span className="ml-2 text-xs" style={{ color: 'var(--text-muted, #8e93a6)' }}>
          ({client.shortName})
        </span>
      )}
      {client.industry && (
        <span className="mt-0.5 block text-xs" style={{ color: 'var(--text-muted, #8e93a6)' }}>
          {client.industry}
        </span>
      )}
    </button>
  )
}

export interface ClientDropdownPanelProps {
  filteredClients: ClientProfile[]
  clientSearch: string
  setClientSearch: (value: string) => void
  showNewClientForm: boolean
  newClientFormProps: NewClientFormProps
  hasCreateClient: boolean
  onShowNewClientForm: () => void
  onSelectNoClient: () => void
  onSelectClient: (id: string) => void
}

function NoClientsFoundMessage(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted, #8e93a6)' }}>
      {t('newCase.noClientsFound')}
    </div>
  )
}

function NoClientButton({ onSelect }: { onSelect: () => void }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full px-4 py-2.5 text-left italic transition-colors"
      style={{ color: 'var(--text-muted, #8e93a6)' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--hover, rgba(78,94,130,0.06))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >
      {t('newCase.noClient')}
    </button>
  )
}

function pickCreateOrFormSection(props: ClientDropdownPanelProps): ReactElement | null {
  if (props.showNewClientForm) return <NewClientForm {...props.newClientFormProps} />
  if (props.hasCreateClient) return <CreateClientButton onClick={props.onShowNewClientForm} />
  return null
}

function ClientDropdownPanel(props: ClientDropdownPanelProps): ReactElement {
  const { t } = useTranslation('cases')
  const showEmptyMessage = props.filteredClients.length === 0 && props.clientSearch
  return (
    <div
      className="absolute z-10 mt-1 max-h-80 w-full overflow-hidden rounded-lg border shadow-lg"
      style={{
        background: 'var(--bg-card, #ffffff)',
        borderColor: 'var(--border, rgba(28,31,46,0.09))',
      }}
    >
      <div className="border-b p-2" style={{ borderColor: 'var(--border, rgba(28,31,46,0.09))' }}>
        <input
          type="text"
          value={props.clientSearch}
          onChange={(e) => {
            props.setClientSearch(e.target.value)
          }}
          placeholder={t('newCase.searchClients')}
          className="w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:ring-[#4e5e82] focus:outline-none"
          style={{ ...INPUT_STYLE, background: 'var(--bg, #f0f1f5)' }}
        />
      </div>
      {pickCreateOrFormSection(props)}
      <div className="max-h-48 overflow-y-auto">
        <NoClientButton onSelect={props.onSelectNoClient} />
        {props.filteredClients.map((client) => (
          <ClientListItem
            key={client.id}
            client={client}
            onSelect={() => {
              props.onSelectClient(client.id)
            }}
          />
        ))}
        {showEmptyMessage && <NoClientsFoundMessage />}
      </div>
    </div>
  )
}

interface ClientPickerProps {
  selectedClient: ClientProfile | undefined
  showClientDropdown: boolean
  toggleDropdown: () => void
  disabled: boolean
  panelProps: ClientDropdownPanelProps
}

export function ClientPicker(props: ClientPickerProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="relative">
      <label
        htmlFor="client"
        className="mb-2 block text-sm font-medium"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('newCase.clientProfile')}
      </label>
      <ClientTriggerButton
        selectedClient={props.selectedClient}
        onClick={props.toggleDropdown}
        disabled={props.disabled}
      />
      {props.showClientDropdown && <ClientDropdownPanel {...props.panelProps} />}
    </div>
  )
}
